import { Component, OnInit } from '@angular/core';
import { BnplService } from '../../shared/services/bnpl.service';

import { ControlBase } from 'src/app/core/shared/models/control-base';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { PersonalInformation } from '../../shared/models/personalinformation.model';
import { Router } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { isSpecialCharacters } from 'src/app/shared/validators/specialCharacters';
import { JuradValidationService } from '../../shared/services/jurad-validation.service';
import { IInitJuradBody } from '../../shared/models/jurad.model';

@Component({
  selector: 'app-personal-information',
  templateUrl: './personal-information.component.html',
  styleUrls: ['./personal-information.component.scss'],
})
export class PersonalInformationComponent implements OnInit {
  formDates!: FormGroup;
  // public controlsPersonalInformation$: Observable<ControlBase<any>[]>;
  days: number[] = Array.from({ length: 31 }, (_, i) => i + 1); // Días del 1 al 31
  months: { value: number; name: string }[] = [
    { value: 1, name: 'Enero' },
    { value: 2, name: 'Febrero' },
    { value: 3, name: 'Marzo' },
    { value: 4, name: 'Abril' },
    { value: 5, name: 'Mayo' },
    { value: 6, name: 'Junio' },
    { value: 7, name: 'Julio' },
    { value: 8, name: 'Agosto' },
    { value: 9, name: 'Septiembre' },
    { value: 10, name: 'Octubre' },
    { value: 11, name: 'Noviembre' },
    { value: 12, name: 'Diciembre' },
  ];
  years: number[] = [];
  bornYears: number[] = [];
  age: number = 65;
  startYear: number = 0;
  addressSuggestions: any[] = [];
  showSuggestions = false;
  private suggestionSelected = false;
  private addressSubscription?: Subscription;
  sessionToken: string = '';
  addressSelected = false;
  selectedAddress: any = null;

  constructor(
    private readonly bnplService: BnplService,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private juradValidationService: JuradValidationService
  ) {
    // this.controlsPersonalInformation$ = this.bnplService.getControlsPersonalInformation();
    const currentYear = new Date().getFullYear();
    this.startYear = currentYear - 18;
    const endYear = this.startYear - 40;
    this.years = Array.from(
      { length: currentYear - endYear + 1 },
      (_, i) => currentYear - i
    );
    this.bnplService.age.subscribe((res: any) => {
      this.age = res - 18;
      this.bornYears = Array.from(
        { length: this.startYear - (this.startYear - this.age) + 1 },
        (_, i) => this.startYear - i
      );
    });
  }

  ngOnInit() {
    this.inicializarFormulario();
    this.setupAddressAutocomplete();
  }

  setupAddressAutocomplete() {
    this.addressSubscription?.unsubscribe();
    this.addressSubscription = this.formDates
      .get('direccion')
      ?.valueChanges.pipe(
        debounceTime(600),
        distinctUntilChanged(),
        switchMap((query) => {
          if (this.addressSelected) {
            this.addressSelected = false;
            return [];
          }

          this.showSuggestions = false;
          if (query) {
            if (!this.sessionToken) {
              this.sessionToken = uuidv4();
            }

            const data = {
              action: 'GOOGLE_PLACES',
              input: query,
              sessionToken: this.sessionToken,
            };
            return this.bnplService.getAddressSuggestions(data);
          }
          return [];
        })
      )
      .subscribe({
        next: (response: any) => {
          let dataLocation = JSON.parse(response);
          this.addressSuggestions =
            dataLocation?.predictions || dataLocation?.results || [];
          this.showSuggestions = this.addressSuggestions.length > 0;
        },
        error: (err: Error) => {
          console.error('Error:', err);
          this.showSuggestions = false;
        },
      });
  }

  selectAddress(suggestion: any) {
    this.addressSelected = true;
    this.selectedAddress = suggestion; // Guarda la dirección seleccionada correctamente

    // Asignar la dirección seleccionada al FormControl sin redundancias
    this.formDates
      .get('direccion')
      ?.setValue(suggestion.description || suggestion.formatted_address);
    this.formDates.get('direccion')?.updateValueAndValidity();

    // Guardar la dirección en los lugares necesarios
    this.sessionToken = '';
    this.bnplService.stepsInformation.secondStep.googleMaps =
      JSON.stringify(suggestion);
    this.formDates.get('googleMaps')?.setValue(JSON.stringify(suggestion));

    this.showSuggestions = false; // Oculta las sugerencias
  }

  ngOnDestroy() {
    this.addressSubscription?.unsubscribe();
  }

  onBlur() {
    setTimeout(() => {
      if (!this.suggestionSelected) {
        this.showSuggestions = false;
      }
      this.suggestionSelected = false; // Resetear bandera
    }, 200);
  }

  inicializarFormulario(): void {
    this.formDates = this.fb.group({
      primerNombre: [
        this.bnplService.personalInformation.name || '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Zs]*$/),
          isSpecialCharacters,
        ],
      ],
      primerApellido: [
        this.bnplService.personalInformation.primerApellido || '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Zs]*$/),
          isSpecialCharacters,
        ],
      ],
      email: [
        this.bnplService.personalInformation.emailFormulario || '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(/^[\w\-\.]+@([\w-]+\.)+[\w-]{2,4}$/),
        ],
      ],
      diaNacimiento: ['', Validators.required],
      mesNacimiento: ['', Validators.required],
      anioNacimiento: ['', Validators.required],
      direccion: ['', [Validators.required, this.googleMapsAddressValidator()]],
      dia: [
        this.bnplService.personalInformation.fechaExpedicion
          ? this.bnplService.personalInformation.fechaExpedicion.split('/')[0]
          : '',
        Validators.required,
      ],
      mes: [
        this.bnplService.personalInformation.fechaExpedicion
          ? this.bnplService.personalInformation.fechaExpedicion.split('/')[1]
          : '',
        Validators.required,
      ],
      anio: [
        this.bnplService.personalInformation.fechaExpedicion
          ? this.bnplService.personalInformation.fechaExpedicion.split('/')[2]
          : '',
        Validators.required,
      ],
      googleMaps: [''],
    });
  }

  sendPersonalInformation() {
    this.bnplService.stepsInformation.secondStep = this.formDates.getRawValue();
    this.handleJuradValidation();
    this.router.navigate(['/selection-fees']);
  }

  preventSpace(event: KeyboardEvent) {
    if (event.code === 'Space') {
      event.preventDefault();
    }
  }

  googleMapsAddressValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        !this.selectedAddress ||
        control.value !==
          (this.selectedAddress.description ||
            this.selectedAddress.formatted_address)
      ) {
        return { notSelected: true }; // Error si el valor escrito no coincide con la selección
      }
      return null;
    };
  }

  handleJuradValidation() {
    const secondStep = this.bnplService.stepsInformation.secondStep;
    const body: IInitJuradBody = {
      actionStrategyPattern: 'INIT_JURAD',
      tipoIdentificacion: 1,
      tipoIdentificacionText: 'CC',
      identificacion:
        this.bnplService.stepsInformation.firstStep.numeroDocumento,
      fechaExpedicion: `${secondStep.dia}/${secondStep.mes}/${secondStep.anio}`,
    };
    this.juradValidationService.juradService(body).subscribe({
      next: (response: any) => {
        console.log('Estado de jurad', response.validationStrategy);
      },
      error: (error) => {
        console.error('Error al preguntar a jurad', error);
      },
    });
  }
}
